import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Student, UpdateStudent } from "../lib/supabase";
import StudentForm from "../components/students/StudentForm";
import SmartBackButton from "../components/ui/SmartBackButton";
import toast from "react-hot-toast";

const EditStudent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [student, setStudent] = useState<Student | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        setIsFetching(true);

        if (!id) return;

        const { data, error } = await supabase
          .from("students")
          .select("*")
          .eq("id", id)
          .single();

        if (error) {
          console.error("Supabase error details:", error);
          throw error;
        }

        console.log("Edit student data from DB:", data);

        if (data) {
          setStudent(data);
        } else {
          setStudent(null);
        }
      } catch (error) {
        console.error("Error fetching student:", error);
        toast.error("Failed to load student details");
      } finally {
        setIsFetching(false);
      }
    };

    fetchStudent();
  }, [id]);

  const handleUpdateStudent = async (data: UpdateStudent) => {
    try {
      setIsLoading(true);

      if (!id) return;

      // Check if run_id is changing
      const oldRunId = student?.run_id;
      const newRunId = data.run_id === "" ? null : data.run_id;

      // Convert camelCase fields to lowercase for the database and handle null values
      const studentData = {
        codername: data.codername,
        age: data.age,
        parentname: data.parentname,
        email: data.email,
        phone: data.phone,
        run_id: newRunId,
        notes: data.notes || null,
        school: data.school || null,
      };

      console.log("Updating student with data:", studentData);

      const { error } = await supabase
        .from("students")
        .update(studentData)
        .eq("id", id);

      if (error) {
        console.error("Supabase update error:", error);
        throw error;
      }

      // Handle payment record updates if run changed
      if (oldRunId !== newRunId) {
        // Delete old payment record if it exists
        if (oldRunId) {
          const { error: deleteError } = await supabase
            .from("payments")
            .delete()
            .eq("student_id", id)
            .eq("run_id", oldRunId);

          if (deleteError) {
            console.error("Error deleting old payment record:", deleteError);
          }
        }

        // Create new payment record if student is assigned to a new run
        if (newRunId) {
          const { error: paymentError } = await supabase
            .from("payments")
            .insert({
              student_id: id,
              run_id: newRunId,
              amount: 0, // 0 amount indicates payment not received
              payment_method: "stripe",
              payment_date: new Date().toISOString().split("T")[0],
              notes:
                "Payment not yet received - student moved to different run",
            });

          if (paymentError) {
            console.error("Error creating new payment record:", paymentError);
            toast.error("Student updated but payment tracking record failed");
          }
        }
      }

      toast.success("Student updated successfully");
      navigate(`/students/${id}`);
    } catch (error) {
      console.error("Error updating student:", error);
      toast.error("Failed to update student");
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="animate-pulse">
        <div className="flex items-center mb-6">
          <div className="w-24 h-8 bg-neutral-200 rounded"></div>
        </div>
        <div className="w-48 h-8 bg-neutral-200 rounded mb-6"></div>
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
            <div className="h-8 bg-neutral-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-12 bg-neutral-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="text-center py-12">
        <h2 className="mb-4">Student Not Found</h2>
        <p className="mb-6">
          The student you are trying to edit does not exist or has been removed.
        </p>
        <SmartBackButton fallbackPath="/students" className="btn btn-primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-6">
        <SmartBackButton
          fallbackPath={`/students/${id}`}
          className="text-link hover:text-hover flex items-center"
        >
          Back to Student Details
        </SmartBackButton>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h1 className="">Edit Student: {student.codername}</h1>
          </div>

          <StudentForm
            defaultValues={student}
            onSubmit={handleUpdateStudent}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default EditStudent;
